import api from "./api";

/**
 * @description Servicio HTTP del módulo de Soporte. Cubre el bot conversacional,
 *              la KB, el inbox del operador, las métricas y la configuración.
 *              Cada función mapea 1-1 a un endpoint del API
 *              (`/support/...`) y devuelve directamente el body de la respuesta.
 *
 *              Las funciones que no existían acá causaban
 *              "supportService.<x> is not a function" al abrir cualquier vista
 *              de Soporte — bug introducido cuando los PR #11/#12/#13
 *              agregaron las views sin tocar este service.
 */
const supportService = {};

/**
 * @description Legacy — botón "Contactar soporte" del Profile. Nada que ver
 *              con el bot nuevo; mandan un mail por el flujo viejo
 *              (`/users/support`). Se mantiene para no romper SupportForm.vue.
 * @param {string} query - texto del usuario
 * @returns {Promise<Object>}
 */
supportService.sendSupportMessage = function (query) {
  return api
    .post("users/support", {
      subject: "Soporte Dashboard",
      query,
    })
    .then((res) => res.data);
};

// --------------------------------------------------------------------------
// KB — Knowledge base del bot. Mapea a `components/support-kb-article/route.js`.
// --------------------------------------------------------------------------

/**
 * @description Lista artículos KB del tenant + globales con paginado/filtros.
 * @param {Object} [params] - {modulo, scope, status, audiencia, q, page, limit}
 * @returns {Promise<{docs: Array, total: number, page: number, limit: number}>}
 */
supportService.listArticles = function (params) {
  return api.get("support/kb/articles", { params }).then((res) => res.data);
};

/**
 * @description Detalle de un artículo KB (sin embedding).
 * @param {string} id
 * @returns {Promise<Object>}
 */
supportService.getArticle = function (id) {
  return api.get(`support/kb/articles/${id}`).then((res) => res.data);
};

/**
 * @description Crea un artículo nuevo. Backend genera embedding antes del save.
 * @param {Object} payload - {titulo, contenidoMarkdown, modulo, audiencia,
 *                            scope, organization?, variantes?, status?}
 * @returns {Promise<Object>} artículo creado
 */
supportService.createArticle = function (payload) {
  return api.post("support/kb/articles", payload).then((res) => res.data);
};

/**
 * @description Edita un artículo. Si cambia titulo/contenido/variantes,
 *              el backend regenera el embedding. Siempre appendea a history.
 * @param {string} id
 * @param {Object} payload
 * @returns {Promise<Object>} artículo actualizado
 */
supportService.updateArticle = function (id, payload) {
  return api.put(`support/kb/articles/${id}`, payload).then((res) => res.data);
};

/**
 * @description Soft delete (status=archived).
 * @param {string} id
 * @returns {Promise<Object>}
 */
supportService.deleteArticle = function (id) {
  return api.delete(`support/kb/articles/${id}`).then((res) => res.data);
};

/**
 * @description Original (snapshot del .md) + estado de drift del artículo.
 * @param {string} id
 * @returns {Promise<{source: Object, drift: Object}>}
 */
supportService.getArticleSource = function (id) {
  return api.get(`support/kb/articles/${id}/source`).then((res) => res.data);
};

/**
 * @description Restaura el contenido al snapshot del .md original.
 *              Regenera embedding y appendea a history.
 * @param {string} id
 * @returns {Promise<Object>}
 */
supportService.restoreArticle = function (id) {
  return api.post(`support/kb/articles/${id}/restore`).then((res) => res.data);
};

/**
 * @description Árbol completo de `docs/kb/` con estado de cada sección
 *              (importada / drift / huérfana).
 * @returns {Promise<{files: Array<{file, sections}>}>}
 */
supportService.listSources = function () {
  return api.get("support/kb/sources").then((res) => res.data);
};

/**
 * @description Importa todas las secciones de un .md a la collection.
 *              Idempotente: las ya importadas vuelven en `skipped`.
 * @param {string} file - ruta relativa al repo (ej. docs/kb/global/agenda/manual.md)
 * @returns {Promise<{imported: Array, skipped: Array}>}
 */
supportService.importSourceFile = function (file) {
  return api
    .post("support/kb/sources/import", { file })
    .then((res) => res.data);
};

// --------------------------------------------------------------------------
// Conversaciones — bot + inbox del operador.
// Mapea a `components/support-conversation/route.js`.
// --------------------------------------------------------------------------

/**
 * @description Abre una conversación nueva para el usuario logueado.
 * @param {Object} context - {route, userAgent, lastErrors, moduleHint, patientId}
 * @param {string} [channel='in_app_chat']
 * @returns {Promise<Object>} SupportConversation
 */
supportService.startConversation = function (context, channel) {
  return api
    .post("support/conversations", { context, channel })
    .then((res) => res.data);
};

/**
 * @description Inbox del operador. Filtros: status, assignedTo
 *              ('me'|<userId>|'unassigned'), q, page, limit.
 * @param {Object} [params]
 * @returns {Promise<{docs: Array, total: number, page: number, limit: number}>}
 */
supportService.listConversations = function (params) {
  return api.get("support/conversations", { params }).then((res) => res.data);
};

/**
 * @description Detalle de una conversación + mensajes.
 * @param {string} id
 * @returns {Promise<{conversation: Object, messages: Array}>}
 */
supportService.getConversation = function (id) {
  return api.get(`support/conversations/${id}`).then((res) => res.data);
};

/**
 * @description Manda un mensaje como el dueño de la conversación. Devuelve
 *              userMsg + botMsg (o null si la conversación ya estaba escalada)
 *              + flag escalated.
 * @param {string} id
 * @param {string} text
 * @returns {Promise<{userMsg: Object, botMsg: Object|null, escalated: boolean}>}
 */
supportService.sendUserMessage = function (id, text) {
  return api
    .post(`support/conversations/${id}/messages`, { text })
    .then((res) => res.data);
};

/**
 * @description Operador responde al usuario.
 * @param {string} id
 * @param {string} text
 * @returns {Promise<Object>} mensaje creado
 */
supportService.sendHumanMessage = function (id, text) {
  return api
    .post(`support/conversations/${id}/human-message`, { text })
    .then((res) => res.data);
};

/**
 * @description Asigna o transfiere la conversación. `to` puede ser un userId,
 *              'me' o falsy (default: el caller).
 * @param {string} id
 * @param {string} [to]
 * @returns {Promise<Object>} conversación actualizada
 */
supportService.assignConversation = function (id, to) {
  return api
    .put(`support/conversations/${id}/assign`, { to })
    .then((res) => res.data);
};

/**
 * @description Fuerza escalación a humano. Type por default `sync_chat`.
 * @param {string} id
 * @param {string} [reason]
 * @param {string} [type]
 * @returns {Promise<Object>}
 */
supportService.escalate = function (id, reason, type) {
  return api
    .put(`support/conversations/${id}/escalate`, { reason, type })
    .then((res) => res.data);
};

/**
 * @description Cierra la conversación con outcome + tags + notas.
 * @param {string} id
 * @param {Object} resolution - {outcome, tags, notes}
 * @returns {Promise<Object>} conversación cerrada
 */
supportService.closeConversation = function (id, resolution) {
  return api
    .put(`support/conversations/${id}/close`, resolution)
    .then((res) => res.data);
};

/**
 * @description Usuario califica la conversación.
 * @param {string} id
 * @param {('positive'|'negative'|Object)} ratingOrBody - string del rating
 *        o body completo `{rating, comment?}`. Se acepta string para
 *        compatibilidad con SupportChatWidget que llama
 *        `rateConversation(id, 'positive')`.
 * @returns {Promise<Object>}
 */
supportService.rateConversation = function (id, ratingOrBody) {
  const body =
    typeof ratingOrBody === "string"
      ? { rating: ratingOrBody }
      : ratingOrBody || {};
  return api
    .post(`support/conversations/${id}/csat`, body)
    .then((res) => res.data);
};

/**
 * @description Polling de mensajes nuevos desde el `lastMessageId`.
 *              Backend devuelve solo los más nuevos.
 * @param {string} id
 * @param {string} lastMessageId
 * @returns {Promise<{messages: Array, conversation?: Object}>}
 */
supportService.pollSince = function (id, lastMessageId) {
  return api
    .get(`support/conversations/${id}/since/${lastMessageId}`)
    .then((res) => res.data);
};

/**
 * @description LLM sugiere la próxima respuesta del operador. Devuelve
 *              `{suggestion}` o vacío si no pudo.
 * @param {string} id
 * @returns {Promise<{suggestion: string, _meta?: Object}>}
 */
supportService.suggestReply = function (id) {
  return api
    .post(`support/conversations/${id}/suggest-reply`)
    .then((res) => res.data);
};

/**
 * @description Métricas agregadas del tenant (counts, costs, CSAT).
 * @param {Object} params - {from, to} (ISO dates)
 * @returns {Promise<Object>}
 */
supportService.getMetrics = function (params) {
  return api
    .get("support/conversations/_/metrics", { params })
    .then((res) => res.data);
};

// --------------------------------------------------------------------------
// Config del bot — Soporte → Configuración.
// Mapea a `components/support-config/route.js`.
// --------------------------------------------------------------------------

/**
 * @description Lee SupportConfig del tenant. apiKey JAMÁS aparece — el
 *              backend devuelve `assistant.hasApiKey: bool`.
 * @returns {Promise<Object>}
 */
supportService.getConfig = function () {
  return api.get("support/config").then((res) => res.data);
};

/**
 * @description Actualiza SupportConfig. apiKey solo se persiste si el body
 *              manda un string no vacío; sino el backend conserva la actual.
 * @param {Object} payload
 * @returns {Promise<Object>} config sanitizada (sin apiKey, con hasApiKey)
 */
supportService.updateConfig = function (payload) {
  return api.put("support/config", payload).then((res) => res.data);
};

export default supportService;
